import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createFolder,
  renameFolder,
  deleteFolder,
  renameFile,
  moveFile,
} from '../api/filesApi';
import { recycleBinKeys } from '../api/recycleBinApi';
import { folderContentsKeys } from './useFolderContents';
import { siteFilesKeys } from './useSiteFiles';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

export function useCreateFolder(siteId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: { name: string; parentFolderId?: string }) =>
      createFolder(siteId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.all,
      });
    },
  });
}

export function useRenameFolder(siteId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      renameFolder(siteId, folderId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.all,
      });
    },
  });
}

export function useDeleteFolder(siteId: number) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  return useMutation({
    mutationFn: (folderId: string) => deleteFolder(siteId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: siteFilesKeys.list(siteId),
      });
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: recycleBinKeys.all,
        refetchType: 'all',
      });
      showSnackbar('Folder deleted', 'success');
    },
  });
}

export function useRenameFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId, name }: { fileId: string; name: string }) =>
      renameFile(fileId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.all,
      });
    },
  });
}

export function useMoveFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      fileId,
      targetFolderId,
    }: {
      fileId: string;
      targetFolderId?: string;
    }) => moveFile(fileId, targetFolderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: folderContentsKeys.all,
      });
    },
  });
}
